/**
 * The self-serve DATA RIGHTS store — export your archive, or request erasure
 * (compliance.md § "Data rights"). This is the in-product half of a promise the
 * marketing site already makes ("export and deletion are self-serve in your
 * settings"): the edge routes and the services behind them existed, and nothing
 * a user could press ever called them.
 *
 * Both dependencies are injected (DIP): the {@link DataRightsGateway} (the real
 * `ApiClient` satisfies it structurally) and the {@link ArchiveSaver} that turns
 * the archive into a file — so this store is exercised in `bun test` with no DOM
 * and no network.
 *
 * After a successful deletion request the account is ALREADY disabled, so every
 * subsequent call answers `403 account_disabled`. The store records the schedule
 * and stops; ending the session is the surface's job, on the user's click, once
 * they have read when their data is erased.
 *
 * Thin + reactive-root-free (only `createSignal`) like the other product stores.
 */

import type { ApiError, Result } from '@4f/shared';
import { isErr } from '@4f/shared';
import { type Accessor, createSignal } from 'solid-js';
import type { DataExportView, DeletionScheduleView } from '../api/types.ts';

/** The edge calls this store depends on — `ApiClient` satisfies it structurally. */
export interface DataRightsGateway {
  exportData(): Promise<Result<DataExportView, ApiError>>;
  requestAccountDeletion(): Promise<Result<DeletionScheduleView, ApiError>>;
}

/** Turns a fetched archive into a saved file (a browser download in the app). */
export interface ArchiveSaver {
  save(fileName: string, archive: DataExportView): void;
}

/**
 * Collaborators the store is built with.
 *
 * There is deliberately NO `onDeleted` hook that drops the session: clearing the
 * session unmounts the whole authenticated subtree (`AuthGate` swaps it for the
 * sign-in surface in the same synchronous update), which includes the settings
 * modal this panel lives in. Signing out automatically therefore made the
 * confirmation — the only place the user ever sees their purge date, since they
 * can never log back in to look — impossible to render. The panel shows the
 * schedule and hands the user an explicit sign-out instead.
 */
export interface DataRightsDeps {
  readonly saver: ArchiveSaver;
}

export interface DataRightsStore {
  /** True while an export or a deletion request is in flight. */
  readonly busy: Accessor<boolean>;
  /** The last failure, or `null`. A `429` is a pace, not a denial — surfaced as-is. */
  readonly error: Accessor<ApiError | null>;
  /** The file name of the most recent successful export, or `null`. */
  readonly savedAs: Accessor<string | null>;
  /** The schedule returned by a successful deletion request, or `null`. */
  readonly deletion: Accessor<DeletionScheduleView | null>;
  /** Fetch the archive and hand it to the saver. */
  exportData(): Promise<void>;
  /** Request erasure. IRREVERSIBLE in-product — the caller must confirm first. */
  requestDeletion(): Promise<void>;
}

/** The archive file name — stamped with the export id so two downloads never collide. */
export function exportFileName(archive: DataExportView): string {
  return `4fantasia-export-${archive.meta.exportId}.json`;
}

/** Build the data-rights store around the injected gateway + saver. */
export function createDataRightsStore(
  gateway: DataRightsGateway,
  deps: DataRightsDeps,
): DataRightsStore {
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<ApiError | null>(null);
  const [savedAs, setSavedAs] = createSignal<string | null>(null);
  const [deletion, setDeletion] = createSignal<DeletionScheduleView | null>(null);

  /** Run one action under the shared busy/error discipline, never overlapping. */
  async function run<T>(
    action: () => Promise<Result<T, ApiError>>,
    onOk: (value: T) => void,
  ): Promise<void> {
    // A second click while a request is in flight would double-charge our own
    // database on export and, worse, re-POST a deletion — guard, don't queue.
    if (busy()) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await action();
      if (isErr(result)) {
        setError(result.error);
        return;
      }
      onOk(result.value);
    } finally {
      setBusy(false);
    }
  }

  return {
    busy,
    error,
    savedAs,
    deletion,
    exportData: () =>
      run(
        () => gateway.exportData(),
        (archive) => {
          const fileName = exportFileName(archive);
          deps.saver.save(fileName, archive);
          setSavedAs(fileName);
        },
      ),
    requestDeletion: () =>
      run(
        () => gateway.requestAccountDeletion(),
        (schedule) => setDeletion(schedule),
      ),
  };
}

/**
 * The browser {@link ArchiveSaver}: serialize the archive to a Blob and click a
 * transient anchor at its object URL. The URL is revoked on the next tick — not
 * immediately, since a synchronous revoke can race the browser's own read of the
 * link it was just asked to download.
 */
export function createBlobArchiveSaver(): ArchiveSaver {
  return {
    save(fileName, archive) {
      const blob = new Blob([JSON.stringify(archive, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
  };
}
